// @flow
import React, { PureComponent } from 'react';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { ThemeType, QuestionType } from 'types/Faq';
import type { FullUserType } from 'types/User';
import { Link } from 'react-router-dom';
import { handleTagEvent }from 'utils/tagManagerUtils';
import { getThemeWithQuestion } from 'utils/faqUtils';
import { getMetaBySlug } from 'utils/seoUtils';

export type StateProps = {
  themes?: ThemeType[],
  questions: QuestionType[],
  userPref: FullUserType
};

type Props = {
  match: {
    params: {
      slugTheme: string,
      slugQuestion: string
    }
  },
  validSearch: string
} & StateProps;

type State = {
  isDisplayed: boolean
}

class FaqContent extends PureComponent <Props, State>{
  state: State = {
    isDisplayed: true
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.isDisplayed === this.state.isDisplayed) {
      this.setState({
        isDisplayed: true
      });
    }
  }

  renderQuestions = () => {
    const { questions, userPref, themes, validSearch } = this.props;
    const { slugQuestion, slugTheme } = this.props.match.params;

    if (!themes || themes.length === 0) {
      return null;
    }

    let filteredTheme = themes.filter(theme => theme.slug === slugTheme);
    if ((!filteredTheme || filteredTheme.length === 0) && validSearch === '') {
      filteredTheme = getThemeWithQuestion(themes, questions);
    }

    const filteredQuestions = questions.filter(question => question.theme === filteredTheme[0].id);
    if (filteredTheme[0].page_liee && filteredTheme[0].page_liee_link) {
      filteredQuestions.push({
        id: filteredTheme[0].page_liee,
        theme: filteredTheme[0].id,
        link: filteredTheme[0].page_liee_link,
        title: 'Voir la rubrique dédiée',
        content: '',
        label: {
          id: filteredTheme[0].id,
          slug: filteredTheme[0].slug,
          name: filteredTheme[0].name,
        },
        meta_description: '',
        meta_title: '',
        menu_order: 0,
        slug: '',
        typeFlow: 'QuestionType',
      });
    }
    const slug = slugTheme || filteredTheme[0].slug;
    return filteredQuestions.sort((a, b) => a.menu_order - b.menu_order).map((question, index) => {
      if (question.slug !== slugQuestion) {
        const questionTheme = themes.filter(theme => theme.id === question.theme)[0];
        if (question.link) {
          return(
            <Link to={question.link} key={index}
              className="link-chevron link-chevron--din">
              <span>{question.title}</span>
              <i className="icon icon-chevron-right"></i>
            </Link>
          );
        }
        return (
          <Link
            key={index}
            to={`/faq/${slug}/${question.slug}`}
            className="link-chevron link-chevron--din"
            onClick={handleTagEvent(
              'FAQ',
              questionTheme.name,
              question.title,
              userPref
            )}>
              <span>{question.title}</span>
              <i className="icon icon-chevron-right"></i>
          </Link>
        );
      }

      return null;
    }).filter((filteredQuestion) => filteredQuestion !== null);
  }

  renderQuestionsSearch = (questions: QuestionType[]):any => {
    const { themes } = this.props;
    if (!themes || themes.length === 0) {
      return null;
    }

    return questions.map((question, index) => {
      const filteredTheme = themes.filter(theme => theme.id === question.theme);
      return (
        <Link
          key={index}
          to={`/faq/${filteredTheme[0].slug}/${question.slug}`}
          className="link-chevron link-chevron--din">
          <span>{question.title}</span>
          <i className="icon icon-chevron-right"></i>
        </Link>
      );
    });
  }

  handledClickStatistics = () => {
    this.setState({
      isDisplayed: false
    });
  }

  renderMainContent = () => {
    const { validSearch } = this.props;
    const { slugTheme, slugQuestion } = this.props.match.params;

    if (validSearch) {
      const { questions } = this.props;
      const filteredQuestion = questions.filter(question =>
        question.title.toLowerCase().includes(validSearch.toLowerCase())
        || question.content.toLowerCase().includes(validSearch.toLowerCase())
      );

      if (!filteredQuestion || filteredQuestion.length === 0) {
        return (
          <div className="block-faq__content js-nofind">
            <h2 className="mb-2">{`Aucun résultat pour « ${validSearch} »`}</h2>
            <p className="ft-secondary mb-4">{"Nous n'avons trouvé aucun résultat pour votre recherche"}</p>
          </div>
        );
      }

      return (
        <div className="block-faq__content js-find">
          <h2 className="mb-2">Resultats de la recherche</h2>
          <p className="ft-secondary mb-4"></p>
          <div className="block-faq__links">
            {this.renderQuestionsSearch(filteredQuestion)}
          </div>
        </div>
      );
    }

    if (!slugQuestion) {
      const { themes, questions } = this.props;

      if (!themes || themes.length === 0) {
        return null;
      }

      let filteredTheme = themes.filter(theme => theme.slug === slugTheme);

      if (filteredTheme.length === 0) {
        filteredTheme = getThemeWithQuestion(themes, questions);
      }
      if (!filteredTheme[0]) {
        return null;
      }

      return (
        <div className="block-faq__content js-issues">
          <h2 className="mb-2">{filteredTheme[0].name}</h2>
          <p className="ft-secondary mb-4">{filteredTheme[0].description}</p>
          <div className="block-faq__links">
            {this.renderQuestions()}
          </div>
        </div>
      );
    } else {
      const { questions, userPref } = this.props;
      const { isDisplayed } = this.state;
      if (!questions || questions.length === 0) {
        return null;
      }
      const filteredQuestion = questions.filter(question => question.slug === slugQuestion);
      if (!filteredQuestion || filteredQuestion.length === 0) {
        return null;
      }
      // TODO : Change to html generated with Contant @WAITING API
      return (
        <div itemScope itemType="https://schema.org/FAQPage">
          <div className="block-faq__content js-find" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h2 className="mb-2" itemProp="name">{filteredQuestion[0].title}</h2>
            <div className="ft-secondary mb-4" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <div itemProp="text" dangerouslySetInnerHTML={{__html: filteredQuestion[0].content}}></div>
            </div>
            {isDisplayed &&
              <p className="ft-secondary mb-4">Avez-vous trouvé cet article utile ?
                <span>
                  <button
                    className="btn btn--white btn--xs"
                    onClick={handleTagEvent(
                      'faq',
                      'clic reponse faq',
                      'Yes',
                      userPref,
                      this.handledClickStatistics
                    )}>oui</button>
                    {' ou '}
                  <button
                    className="btn btn--white btn--xs"
                    onClick={handleTagEvent(
                      'faq',
                      'clic reponse faq',
                      'No',
                      userPref, 
                      this.handledClickStatistics
                    )}>non</button>
                </span>
              </p>
            }
            <p className="ft-h3">Sur le même sujet</p>
            <div className="block-faq__links">
              {this.renderQuestions()}
            </div>
          </div>
        </div>
      );
    }
  }

  render(){
    const { themes } = this.props;
    const { slugTheme } = this.props.match.params;

    if (!themes || themes.length === 0) {
      return null;
    }

    const filteredTheme = themes.filter(theme => theme.slug === slugTheme);

    return (
      <div className="col-md-8 col-lg-9">
        {this.renderMainContent()}
        <div className="block-faq__write">
          <h3 className="mb-1">Vous ne trouvez pas ce que vous cherchez ?</h3>
          <p className="mb-2">Nous pouvons vous aider à trouver une réponse</p>
          <Link to={{
            pathname: `/contact/nous-contacter`,
            state: {
              object: filteredTheme[0] ? filteredTheme[0].name : '',
            },
          }}>
          <button className="btn btn--primary js-contact-write">
            Contactez-nous
          </button>
        </Link>
        </div>
      </div>
    );
  }
}

export default withHelmet(FaqContent)((Helmet, pageProps) => {
  if (pageProps.match.params.slugQuestion) {
    const question = pageProps.questions.find(question => pageProps.match.params.slugQuestion === question.slug);
    if (question) {
      return (
        <Helmet>
          <title>{question.meta_title}</title>
          <meta name={'description'} content={question.meta_description} />
          <meta property={'og:title'} content={question.meta_title} />
          <meta property={'og:description'} content={question.meta_description} />
        </Helmet>
      );
    }
  } else if (pageProps.match.params.slugTheme) {
    const meta = getMetaBySlug(`faq/slugTheme`);
    const theme = pageProps.themes.find(theme => pageProps.match.params.slugTheme === theme.slug);
    if (theme) {
      return (
        <Helmet>
          <title>{meta.title(theme.name)}</title>
          <meta name={'description'} content={meta.description(theme.name)} />
          <meta property={'og:title'} content={meta.title(theme.name)} />
          <meta property={'og:description'} content={meta.description(theme.name)} />
        </Helmet>
      );
    }
  }
  return null;
});
